import { useAuth0 } from '@auth0/auth0-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  addProjectMember,
  createManagedProject,
  deleteManagedProject,
  getManagedProject,
  listManagedProjects,
  removeProjectMember,
  updateManagedProject,
  updateProjectMember,
  type AddProjectMemberInput,
  type CreateManagedProjectInput,
  type UpdateManagedProjectInput,
  type UpdateProjectMemberInput,
} from '~/data/endpoints/managed.projects';

export const managedProjectKeys = {
  all: ['managed-projects'] as const,
  list: (organizationId: string | undefined, params?: { isActive?: boolean; mineOnly?: boolean; search?: string }) =>
    [...managedProjectKeys.all, organizationId, params] as const,
  detail: (organizationId: string | undefined, id: string) => [...managedProjectKeys.all, organizationId, id] as const,
  styleGuide: (organizationId: string | undefined, id: string) =>
    [...managedProjectKeys.all, organizationId, id, 'styleGuide'] as const,
};

export function useManagedProjects(params?: { isActive?: boolean; mineOnly?: boolean; search?: string }) {
  const { user, isAuthenticated, isLoading } = useAuth0();
  const organizationId = user?.org_id;

  return useQuery({
    queryKey: managedProjectKeys.list(organizationId, params),
    queryFn: () => listManagedProjects(params),
    enabled: isAuthenticated && !isLoading && !!organizationId,
  });
}

export function useManagedProject(id: string | undefined, options?: { refetchInterval?: number | false }) {
  const { user, isAuthenticated, isLoading } = useAuth0();
  const organizationId = user?.org_id;

  return useQuery({
    queryKey: managedProjectKeys.detail(organizationId, id ?? ''),
    queryFn: () => {
      if (!id) throw new Error('Project ID is required');
      return getManagedProject(id);
    },
    enabled: isAuthenticated && !isLoading && !!id && !!organizationId,
    refetchInterval: options?.refetchInterval,
  });
}

export function useCreateManagedProject() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateManagedProjectInput) => createManagedProject(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: managedProjectKeys.all });
    },
  });
}

export function useUpdateManagedProject() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateManagedProjectInput }) => updateManagedProject(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: managedProjectKeys.all });
    },
  });
}

export function useDeleteManagedProject() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deleteManagedProject(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: managedProjectKeys.all });
    },
  });
}

export function useAddProjectMember() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ projectId, data }: { projectId: string; data: AddProjectMemberInput }) =>
      addProjectMember(projectId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: managedProjectKeys.all });
    },
  });
}

export function useUpdateProjectMember() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      projectId,
      memberId,
      data,
    }: {
      projectId: string;
      memberId: string;
      data: UpdateProjectMemberInput;
    }) => updateProjectMember(projectId, memberId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: managedProjectKeys.all });
    },
  });
}

export function useRemoveProjectMember() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ projectId, memberId }: { projectId: string; memberId: string }) =>
      removeProjectMember(projectId, memberId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: managedProjectKeys.all });
    },
  });
}
